/**
 * Tier-aware field visibility for the vendor assessment questionnaire (#963).
 *
 * The assessment form (`VendorAssessmentDialog`) renders a fixed set of fields.
 * Rather than asking every diligence question for every vendor, we show:
 *   - CORE  fields for every vendor (the essentials you always ask),
 *   - HIGH  fields only for HIGH / CRITICAL inherent-risk vendors (deeper diligence),
 *   - HIPAA fields only when the workspace has HIPAA enabled.
 *
 * A field is also always shown if it already holds a value, so historical
 * assessments stay fully readable even if the vendor's tier later changes.
 *
 * `FIELD_TIERS` is the single tunable source of truth — adjust a field's tier
 * here to change when it appears. Keys are the section-draft field names used
 * in `VendorAssessmentDialog`. Anything not listed defaults to CORE.
 */

export type FieldTier = 'CORE' | 'HIGH' | 'HIPAA'

export const FIELD_TIERS: Record<string, FieldTier> = {
  // ── Security ──
  encryptionAtRest: 'CORE',
  encryptionInTransit: 'CORE',
  mfaEnforced: 'CORE',
  vulnerabilityScanning: 'CORE',
  incidentResponsePlan: 'CORE',
  penTestFrequency: 'HIGH',
  accessControlModel: 'HIGH',
  // RFC-231 §3.4 — extracted by the AI, previously never rendered.
  lastPentestDate: 'HIGH',
  hostingProvider: 'HIGH',

  // ── Data Handling ──
  dataClassification: 'CORE',
  retentionPolicy: 'CORE',
  dpaInPlace: 'CORE',
  crossBorderTransfer: 'HIGH',
  deletionProcess: 'HIGH',
  subprocessors: 'HIGH',
  dataTypes: 'CORE',
  ephiInvolved: 'HIPAA',
  baaAvailable: 'HIPAA',

  // ── Compliance ──
  soc2: 'CORE',
  iso27001: 'CORE',
  gdpr: 'CORE',
  hipaa: 'HIPAA',
  auditReportsReviewed: 'HIGH',
  otherCerts: 'HIGH',

  // ── Contractual ──
  slaTerms: 'CORE',
  terminationClause: 'CORE',
  liabilityCap: 'HIGH',
  dataReturnOnTermination: 'HIGH',
  insuranceCoverage: 'HIGH',
  dpaStatus: 'HIGH',
  breachNotificationPeriod: 'HIGH',
  auditRightsGranted: 'HIGH',
  autoRenewal: 'HIGH',
  terminationForConvenience: 'HIGH',
  subprocessorChangeNotification: 'HIGH',
  transferMechanism: 'HIGH',

  // Per-section free-text notes are always available.
  notes: 'CORE',
}

export function isHighRiskTier(tier: string | null | undefined): boolean {
  return tier === 'HIGH' || tier === 'CRITICAL'
}

/** True when a value counts as "filled in" (so we never hide saved answers). */
export function hasFieldValue(value: unknown): boolean {
  if (value == null) return false
  if (typeof value === 'string') return value.trim().length > 0
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === 'object')
    return Object.values(value as Record<string, unknown>).some(hasFieldValue)
  return true
}

/**
 * Decide whether a questionnaire field should render.
 *
 * @param fieldKey       section-draft field name
 * @param vendorTier     vendor's inherent risk tier (CRITICAL/HIGH/MEDIUM/LOW)
 * @param hipaaEnabled   whether HIPAA is enabled for the workspace
 * @param value          current value of the field (kept visible if present)
 */
export function shouldShowField(
  fieldKey: string,
  vendorTier: string | null | undefined,
  hipaaEnabled: boolean,
  value: unknown,
): boolean {
  const tier = FIELD_TIERS[fieldKey] ?? 'CORE'
  if (tier === 'CORE') return true
  if (hasFieldValue(value)) return true
  if (tier === 'HIPAA') return hipaaEnabled
  return isHighRiskTier(vendorTier)
}

/**
 * Build a `show(fieldKey)` predicate bound to one tab's draft data + context.
 * Each questionnaire tab reads its own `data`, so the binding stays per-tab.
 */
export function makeFieldVisibility(
  data: Record<string, unknown>,
  vendorTier: string | null | undefined,
  hipaaEnabled: boolean,
): (fieldKey: string) => boolean {
  return (fieldKey) =>
    shouldShowField(fieldKey, vendorTier, hipaaEnabled, data[fieldKey])
}
